import type { Message } from 'ai'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  COPILOT_CURRENT_CONVERSATION_KEY,
  COPILOT_MESSAGES_STORAGE_KEY,
} from '@/features/copilot/constants'
import {
  buildConversationTitle,
  type CopilotConversation,
  type CopilotUserId,
  deleteConversation,
  generateConversationId,
  getConversation,
  listConversations,
  renameConversation,
  subscribeToConversationChanges,
  upsertConversation,
} from '@/features/copilot/copilotConversationStore'

const readCurrentId = (): string | null => {
  try {
    return window.localStorage.getItem(COPILOT_CURRENT_CONVERSATION_KEY)
  } catch (error) {
    console.error('Failed to read current copilot conversation id:', error)
    return null
  }
}

const writeCurrentId = (id: string | null): void => {
  try {
    if (id === null) {
      window.localStorage.removeItem(COPILOT_CURRENT_CONVERSATION_KEY)
    } else {
      window.localStorage.setItem(COPILOT_CURRENT_CONVERSATION_KEY, id)
    }
  } catch (error) {
    console.error('Failed to write current copilot conversation id:', error)
  }
}

const readLegacyMessages = (): Message[] => {
  try {
    const raw = window.localStorage.getItem(COPILOT_MESSAGES_STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as Message[]
    return parsed.map((m) => ({
      ...m,
      createdAt: m.createdAt ? new Date(m.createdAt) : undefined,
    }))
  } catch (error) {
    console.error('Failed to read legacy copilot messages:', error)
    return []
  }
}

const clearLegacyMessages = (): void => {
  try {
    window.localStorage.removeItem(COPILOT_MESSAGES_STORAGE_KEY)
  } catch (error) {
    console.error('Failed to clear legacy copilot messages:', error)
  }
}

export interface UseCopilotConversationsResult {
  conversations: CopilotConversation[]
  currentId: string | null
  current: CopilotConversation | null
  isReady: boolean
  remoteSyncCount: number
  newChat: () => void
  selectChat: (id: string) => Promise<void>
  deleteChat: (id: string) => Promise<void>
  renameChat: (id: string, title: string) => Promise<void>
  saveMessages: (
    messages: Message[],
    meta?: { interfaceId?: number | null; interfaceName?: string | null },
  ) => Promise<string | null>
}

export const useCopilotConversations = (params: {
  userId: CopilotUserId
}): UseCopilotConversationsResult => {
  const { userId } = params
  const [conversations, setConversations] = useState<CopilotConversation[]>([])
  const [currentId, setCurrentId] = useState<string | null>(null)
  const [current, setCurrent] = useState<CopilotConversation | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [remoteSyncCount, setRemoteSyncCount] = useState(0)
  const currentIdRef = useRef<string | null>(null)
  const userIdRef = useRef<CopilotUserId>(userId)

  useEffect(() => {
    currentIdRef.current = currentId
  }, [currentId])

  useEffect(() => {
    userIdRef.current = userId
  }, [userId])

  const refreshList = useCallback(async () => {
    const list = await listConversations(userId)
    setConversations(list)
    return list
  }, [userId])

  const selectChat = useCallback(
    async (id: string) => {
      const conv = await getConversation(id, userId)
      setCurrent(conv)
      setCurrentId(conv ? conv.id : null)
      writeCurrentId(conv ? conv.id : null)
    },
    [userId],
  )

  const newChat = useCallback(() => {
    setCurrent(null)
    setCurrentId(null)
    writeCurrentId(null)
  }, [])

  const deleteChat = useCallback(
    async (id: string) => {
      await deleteConversation(id, userId)
      const next = await refreshList()
      setCurrentId((prevId) => {
        if (prevId !== id) return prevId
        const fallback = next[0]?.id ?? null
        writeCurrentId(fallback)
        return fallback
      })
      setCurrent((prev) => {
        if (prev?.id !== id) return prev
        return next[0] ?? null
      })
    },
    [refreshList, userId],
  )

  const renameChat = useCallback(
    async (id: string, title: string) => {
      const trimmed = title.trim() || 'New conversation'
      await renameConversation(id, trimmed, userId)
      await refreshList()
      setCurrent((prev) =>
        prev && prev.id === id
          ? { ...prev, title: trimmed, updatedAt: Date.now() }
          : prev,
      )
    },
    [refreshList, userId],
  )

  const saveMessages = useCallback<
    UseCopilotConversationsResult['saveMessages']
  >(
    async (messages, meta) => {
      if (messages.length === 0) return null
      const now = Date.now()
      const activeId = currentId ?? generateConversationId()
      const isNew = !currentId
      const existing = isNew ? null : await getConversation(activeId, userId)
      const conv: CopilotConversation = {
        id: activeId,
        title: existing?.title || buildConversationTitle(messages),
        interfaceId: meta?.interfaceId ?? existing?.interfaceId ?? null,
        interfaceName: meta?.interfaceName ?? existing?.interfaceName ?? null,
        scope: existing?.scope,
        userId,
        createdAt: existing?.createdAt ?? now,
        updatedAt: now,
        messages,
      }
      await upsertConversation(conv)
      if (isNew) {
        setCurrentId(activeId)
        writeCurrentId(activeId)
      }
      setCurrent(conv)
      await refreshList()
      return activeId
    },
    [currentId, refreshList, userId],
  )

  useEffect(() => {
    if (!isReady) return
    const unsub = subscribeToConversationChanges(() => {
      ;(async () => {
        try {
          const uid = userIdRef.current
          const list = await listConversations(uid)
          setConversations(list)
          const activeId = currentIdRef.current
          if (!activeId) return
          if (!list.some((c) => c.id === activeId)) {
            setCurrent(null)
            setCurrentId(null)
            writeCurrentId(null)
            setRemoteSyncCount((v) => v + 1)
            return
          }
          const fresh = await getConversation(activeId, uid)
          if (fresh) {
            setCurrent(fresh)
            setRemoteSyncCount((v) => v + 1)
          }
        } catch (err) {
          console.error('Failed to apply remote copilot change:', err)
        }
      })()
    })
    return unsub
  }, [isReady])

  // Re-initialises whenever userId changes (account switch). Resets state,
  // refetches the per-user list, optionally migrates legacy single-conv
  // localStorage to the active user, and restores currentId if it still
  // belongs to that user.
  useEffect(() => {
    let cancelled = false
    setIsReady(false)
    setConversations([])
    setCurrent(null)
    setCurrentId(null)
    let migrated = false
    ;(async () => {
      const list = await listConversations(userId)
      if (cancelled) return
      setConversations(list)

      if (!migrated && list.length === 0 && userId !== null) {
        const legacy = readLegacyMessages()
        if (legacy.length > 0) {
          migrated = true
          const id = generateConversationId()
          const now = Date.now()
          const migratedConv: CopilotConversation = {
            id,
            title: buildConversationTitle(legacy),
            createdAt: now,
            updatedAt: now,
            messages: legacy,
            interfaceId: null,
            interfaceName: null,
            userId,
          }
          await upsertConversation(migratedConv)
          clearLegacyMessages()
          if (cancelled) return
          setCurrentId(id)
          writeCurrentId(id)
          setCurrent(migratedConv)
          const refreshed = await listConversations(userId)
          if (cancelled) return
          setConversations(refreshed)
          setIsReady(true)
          return
        }
      }

      if (cancelled) return

      const storedId = readCurrentId()
      if (storedId && list.some((c) => c.id === storedId)) {
        const conv = await getConversation(storedId, userId)
        if (cancelled) return
        setCurrentId(storedId)
        setCurrent(conv)
      } else if (storedId && userId !== null) {
        // Only wipe the stored id when we are sure it does not belong to the
        // active user. On the first mount Redux may not have hydrated yet
        // (userId === null), so `list` is empty and the storedId would be
        // wrongly discarded — preventing auto-restore on page refresh.
        writeCurrentId(null)
      }
      if (cancelled) return
      setIsReady(true)
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  return {
    conversations,
    currentId,
    current,
    isReady,
    remoteSyncCount,
    newChat,
    selectChat,
    deleteChat,
    renameChat,
    saveMessages,
  }
}
